import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import type { ArrivalTypeRecord } from "@/types/camping";

export function useArrivalTypes(options?: { includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  const [types, setTypes] = useState<ArrivalTypeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const query = includeInactive ? "?include_inactive=true" : "";
    return api
      .get<ArrivalTypeRecord[]>(`/api/v1/arrival-types${query}`)
      .then(setTypes)
      .catch((e: Error) => {
        setError(e.message);
        setTypes([]);
      })
      .finally(() => setLoading(false));
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const byCode = new Map(types.map((t) => [t.code, t]));

  function labelFor(code: string | null | undefined): string {
    if (!code) return "—";
    return byCode.get(code)?.name ?? code;
  }

  function requiresVehicleLength(code: string | null | undefined): boolean {
    if (!code) return false;
    return byCode.get(code)?.requires_vehicle_length ?? false;
  }

  return {
    types,
    loading,
    error,
    reload: load,
    labelFor,
    requiresVehicleLength,
  };
}
